import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Power, Check, X, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PromoPost } from "./PromoPost";

const isPromoExpired = (of) => of.promo_until && of.promo_until < new Date().toISOString().slice(0, 10);

const emptyForm = { name: "", price: "", detail_fr: "", detail_en: "", promo_until: "" };
const inputCls =
  "w-full bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300";

export const OffersPanel = () => {
  const { lang, t } = useLang();
  const o = t.dash.offers;
  const [offers, setOffers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const res = await api.get("/offers");
    setOffers(res.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (category) => {
    if (!form.name.trim() || !form.price.trim()) return;
    if (editing.startsWith("new-")) await api.post("/offers", { ...form, category });
    else await api.patch(`/offers/${editing}`, form);
    setEditing(null);
    setForm(emptyForm);
    load();
  };

  const toggle = async (of) => {
    await api.patch(`/offers/${of.id}`, { active: !of.active });
    setOffers((os) => os.map((x) => (x.id === of.id ? { ...x, active: !x.active } : x)));
  };

  const remove = async (id) => {
    await api.delete(`/offers/${id}`);
    setOffers((os) => os.filter((x) => x.id !== id));
  };

  const renderForm = (category) => (
    <div key={`form-${category}`} className="px-5 py-4 border-b border-white/5 bg-white/[0.02]" data-testid="offer-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input value={form.name} onChange={set("name")} placeholder={o.name} className={inputCls} data-testid="offer-form-name" />
        <input value={form.price} onChange={set("price")} placeholder={o.price} className={inputCls} data-testid="offer-form-price" />
        <input value={form.detail_fr} onChange={set("detail_fr")} placeholder={o.detailFr} className={inputCls} data-testid="offer-form-detail-fr" />
        <input value={form.detail_en} onChange={set("detail_en")} placeholder={o.detailEn} className={inputCls} data-testid="offer-form-detail-en" />
        <div className="flex items-center gap-3">
          <label className="text-xs text-white/40 whitespace-nowrap">{o.promoUntil}</label>
          <input
            type="date"
            value={form.promo_until}
            onChange={set("promo_until")}
            className={`${inputCls} [color-scheme:dark]`}
            data-testid="offer-form-promo-until"
          />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button
          onClick={() => save(category)}
          data-testid="offer-form-save"
          className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-300"
        >
          <Check className="w-3.5 h-3.5" /> {o.save}
        </button>
        <button
          onClick={() => {
            setEditing(null);
            setForm(emptyForm);
          }}
          data-testid="offer-form-cancel"
          className="inline-flex items-center gap-2 border border-white/15 text-white/60 hover:text-white text-xs px-4 py-2 rounded-full transition-colors duration-300"
        >
          <X className="w-3.5 h-3.5" /> {o.cancel}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="offers-panel">
      <p className="text-sm text-white/40">{o.subtitle}</p>
      {offers.some(isPromoExpired) && (
        <div className="flex items-center gap-3 border border-red-500/40 bg-red-500/10 text-red-400 px-4 py-3 text-sm" data-testid="promo-expired-alert">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {offers.filter(isPromoExpired).length} {o.expiredAlert}
        </div>
      )}
      {["orange", "eneco"].map((cat) => {
        const items = offers.filter((of) => of.category === cat);
        return (
          <div key={cat} className="border border-white/10 bg-[#111111]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h3 className="font-display font-semibold">{o[cat]}</h3>
              <button
                onClick={() => {
                  setEditing(`new-${cat}`);
                  setForm(emptyForm);
                }}
                data-testid={`offer-add-${cat}`}
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] text-xs px-4 py-2 rounded-full transition-colors duration-300"
              >
                <Plus className="w-3.5 h-3.5" /> {o.add}
              </button>
            </div>
            <div>
              {items.length === 0 && editing !== `new-${cat}` && (
                <p className="px-5 py-8 text-center text-sm text-white/40">{o.empty}</p>
              )}
              {items.map((of) =>
                editing === of.id ? (
                  renderForm(cat)
                ) : (
                  <div
                    key={of.id}
                    className={`flex items-start justify-between gap-4 px-5 py-4 border-b border-white/5 ${!of.active ? "opacity-40" : ""}`}
                    data-testid={`offer-row-${of.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-sm">
                        {of.name} <span className="text-[#FF7900] ml-2 font-normal">{of.price}</span>
                        {isPromoExpired(of) ? (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-red-500/15 text-red-400 border border-red-500/40 px-2 py-0.5 rounded-full" data-testid={`offer-promo-expired-${of.id}`}>
                            <AlertTriangle className="w-3 h-3" /> {o.promoExpired}
                          </span>
                        ) : of.promo_until ? (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-white/30" data-testid={`offer-promo-until-${of.id}`}>
                            {o.promoActive} {of.promo_until}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-white/50 mt-1">{lang === "fr" ? of.detail_fr : of.detail_en}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 pt-0.5">
                      <button
                        onClick={() => toggle(of)}
                        data-testid={`offer-toggle-${of.id}`}
                        title={of.active ? o.activeLabel : o.inactiveLabel}
                        className={`transition-colors duration-300 ${of.active ? "text-emerald-400 hover:text-white/40" : "text-white/30 hover:text-emerald-400"}`}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditing(of.id);
                          setForm({ name: of.name, price: of.price, detail_fr: of.detail_fr, detail_en: of.detail_en, promo_until: of.promo_until || "" });
                        }}
                        data-testid={`offer-edit-${of.id}`}
                        className="text-white/30 hover:text-[#FF7900] transition-colors duration-300"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(of.id)}
                        data-testid={`offer-delete-${of.id}`}
                        className="text-white/30 hover:text-red-400 transition-colors duration-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )
              )}
              {editing === `new-${cat}` && renderForm(cat)}
            </div>
          </div>
        );
      })}
      <PromoPost offers={offers} />
    </div>
  );
};
